import { useRessource } from '../hooks/useRessource';
import { api } from '../lib/apiClient';
import { Chargement, Erreur, Infobulle } from '../components/Elements';
import type { DashboardActivite as Donnees } from '../types';

/**
 * Dashboard ACTIVITE — le pilotage non financier, maquette validee le
 * 2026-08-23. Aucun montant : effectifs, pyramide des ages, familles
 * d'activite, arrondissements, anniversaires, handicap, equipe.
 *
 * Les blocs soumis a droits arrivent a `null` du serveur et ne
 * s'affichent pas : anniversaires (vue restreinte RG-43), volet equipe
 * des anniversaires (donnee de paie).
 */

const COULEUR_HOMMES = '#3f6ebe';
const COULEUR_FEMMES = '#c2703d';

/**
 * 6 035 minutes → « 100h 35min ».
 *
 * Les unites sont grises et plus petites que les nombres : c'est le
 * volume qu'on lit, pas la mention « h ». Les minutes gardent deux
 * chiffres — « 100h 5min » se lirait mal a cote de « 100h 35min ».
 */
function HeuresMinutes({ minutes }: { minutes: number }) {
  const unite = 'text-xl font-medium text-encre-400';
  return (
    <>
      {Math.floor(minutes / 60)}
      <span className={unite}>h</span>{' '}
      {String(minutes % 60).padStart(2, '0')}
      <span className={unite}>min</span>
    </>
  );
}

const LIBELLES_TRANCHES: Record<string, string> = {
  '19+': 'Adultes',
  '11-18': '11–18 ans',
  '7-10': '7–10 ans',
  '1-6': '1–6 ans',
};

export function DashboardActivite({
  cle,
  vue,
  anneeId,
}: {
  /** Entite de la session — sert de reference pour `vue`. */
  cle: string;
  /** Entite regardee ; `null` = vue cumulee (personnes dedoublonnees). */
  vue: string | null;
  anneeId: string;
}) {
  const { donnees, chargement, erreur, recharger } = useRessource(
    () =>
      api.get<Donnees>('/api/tableau-bord/activite', {
        anneeId,
        cumule: vue === null ? 'true' : undefined,
        entiteId: vue && vue !== cle ? vue : undefined,
      }),
    [cle, vue, anneeId],
  );

  if (chargement) return <Chargement />;
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees) return null;

  const { synthese } = donnees;

  return (
    <>
      {donnees.cumule && donnees.entites && (
        <p className="mb-4 text-xs text-amber-700">
          Vue cumulée — {donnees.entites.map((e) => e.code).join(' + ')}. Les personnes ne sont
          comptées qu’une fois : un élève inscrit dans les deux écoles reste un seul usager.
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TuileActivite
          libelle="Membres usagers"
          valeur={String(synthese.usagers)}
          detail={`dont ${synthese.membres_actifs} membres actifs`}
        />
        <TuileActivite
          libelle="Heures de cours par semaine"
          valeur={<HeuresMinutes minutes={synthese.minutes_semaine} />}
          detail={`réparties sur ${synthese.activites} activités`}
        />
        <TuileActivite
          libelle="Handicap"
          valeur={String(synthese.handicap)}
          detail="membres en situation de handicap"
        />
        {/* Les anniversaires ferment la rangee : c'est la tuile qu'on
            regarde en dernier, et celle qui appelle un geste. */}
        {synthese.anniversaires !== null && (
          <TuileActivite libelle="Anniversaires à venir" valeur={String(synthese.anniversaires)} />
        )}
      </div>

      {/* Deux colonnes, appariees par sujet : les personnes en haut
          (ages, equipe), le territoire et l'offre au milieu. */}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Pyramide donnees={donnees} />
        <Equipe equipe={donnees.equipe} />
        <Arrondissements zones={donnees.arrondissements} />
        <Familles familles={donnees.familles} total={synthese.inscriptions_actives} />
        {donnees.anniversaires !== null && (
          <div className="lg:col-span-2">
            <Anniversaires lignes={donnees.anniversaires} />
          </div>
        )}
      </div>
    </>
  );
}

function TuileActivite({
  libelle,
  valeur,
  detail,
}: {
  libelle: string;
  /** Un nombre le plus souvent ; du balisage quand il porte des unites. */
  valeur: React.ReactNode;
  /** Facultatif : certaines tuiles se suffisent de leur nombre. */
  detail?: string;
}) {
  return (
    <div className="carte h-full p-5">
      <div className="text-xs font-medium uppercase tracking-wide text-encre-400">{libelle}</div>
      <div className="tabulaire mt-2 text-3xl font-semibold text-encre-900">{valeur}</div>
      {detail && <div className="mt-1 text-xs text-encre-500">{detail}</div>}
    </div>
  );
}

function Carte({
  titre,
  sousTitre,
  children,
}: {
  titre: string;
  /** Facultatif : certaines cartes se passent d'explication. */
  sousTitre?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="carte flex h-full flex-col p-5">
      <h2 className="text-sm font-semibold text-encre-900">{titre}</h2>
      {sousTitre ? (
        <p className="mb-4 mt-0.5 text-xs text-encre-500">{sousTitre}</p>
      ) : (
        <div className="mb-4" />
      )}
      {children}
    </section>
  );
}

function Pyramide({ donnees }: { donnees: Donnees }) {
  const lignes = donnees.pyramide.lignes;
  const plafond = Math.max(1, ...lignes.flatMap((l) => [l.hommes, l.femmes]));
  const sansCivilite = lignes.reduce((somme, l) => somme + l.sans_civilite, 0);

  return (
    <Carte titre="Pyramide des âges des adhérents">
      <div className="grid gap-2">
        {lignes.map((ligne) => (
          <div key={ligne.tranche} className="grid grid-cols-[1fr_72px_1fr] items-center gap-2">
            <Infobulle
              balise="div"
              className="relative h-4 rounded bg-encre-50"
              texte={`Hommes ${LIBELLES_TRANCHES[ligne.tranche] ?? ligne.tranche} : ${ligne.hommes}`}
            >
              <span
                className="absolute inset-y-0 right-0 rounded-l"
                style={{
                  width: `${(ligne.hommes / plafond) * 88}%`,
                  minWidth: ligne.hommes > 0 ? 2 : 0,
                  background: COULEUR_HOMMES,
                }}
              />
              <span
                className="tabulaire absolute top-1/2 -translate-y-1/2 text-[11px] text-encre-500"
                style={{ right: `calc(${(ligne.hommes / plafond) * 88}% + 6px)` }}
              >
                {ligne.hommes}
              </span>
            </Infobulle>
            <div className="text-center text-xs text-encre-500">
              {LIBELLES_TRANCHES[ligne.tranche] ?? ligne.tranche}
            </div>
            <Infobulle
              balise="div"
              className="relative h-4 rounded bg-encre-50"
              texte={`Femmes ${LIBELLES_TRANCHES[ligne.tranche] ?? ligne.tranche} : ${ligne.femmes}`}
            >
              <span
                className="absolute inset-y-0 left-0 rounded-r"
                style={{
                  width: `${(ligne.femmes / plafond) * 88}%`,
                  minWidth: ligne.femmes > 0 ? 2 : 0,
                  background: COULEUR_FEMMES,
                }}
              />
              <span
                className="tabulaire absolute top-1/2 -translate-y-1/2 text-[11px] text-encre-500"
                style={{ left: `calc(${(ligne.femmes / plafond) * 88}% + 6px)` }}
              >
                {ligne.femmes}
              </span>
            </Infobulle>
          </div>
        ))}
      </div>
      {/* `mt-auto` sur les DEUX legendes : les cartes d'une meme rangee
          ont la meme hauteur, leurs legendes se retrouvent donc au meme
          niveau quelle que soit la hauteur du contenu. */}
      <div className="mt-auto flex flex-wrap gap-4 pt-3 text-xs text-encre-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm" style={{ background: COULEUR_HOMMES }} /> Hommes
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm" style={{ background: COULEUR_FEMMES }} /> Femmes
        </span>
        <span className="ml-auto">
          {donnees.pyramide.sans_date > 0 && `${donnees.pyramide.sans_date} sans date`}
          {donnees.pyramide.sans_date > 0 && sansCivilite > 0 && ' · '}
          {sansCivilite > 0 && `${sansCivilite} sans civilité`}
        </span>
      </div>
    </Carte>
  );
}

function BarresHorizontales({
  lignes,
}: {
  lignes: { nom: string; valeur: number; detail?: string }[];
}) {
  const plafond = Math.max(1, ...lignes.map((l) => l.valeur));
  return (
    <div className="grid gap-1.5">
      {lignes.map((ligne) => (
        <Infobulle
          key={ligne.nom}
          balise="div"
          className="grid grid-cols-[130px_1fr_44px] items-center gap-2.5"
          texte={ligne.detail ?? `${ligne.nom} : ${ligne.valeur}`}
        >
          <span className="truncate text-xs text-encre-700">{ligne.nom}</span>
          <span className="relative h-3.5 rounded bg-encre-50">
            <span
              className="absolute inset-y-0 left-0 rounded bg-encre-600"
              style={{ width: `${(ligne.valeur / plafond) * 100}%`, minWidth: 2 }}
            />
          </span>
          <span className="tabulaire text-right text-xs text-encre-900">{ligne.valeur}</span>
        </Infobulle>
      ))}
    </div>
  );
}

function Familles({
  familles,
  total,
}: {
  familles: Donnees['familles'];
  total: number;
}) {
  return (
    <Carte
      titre="Inscriptions actives par famille d’activité"
      sousTitre={`${total} inscriptions`}
    >
      <BarresHorizontales
        lignes={familles.map((f) => ({
          nom: f.famille,
          valeur: f.inscriptions,
          detail: `${f.famille} : ${f.inscriptions} inscriptions, ${f.personnes} personnes`,
        }))}
      />
    </Carte>
  );
}

function Arrondissements({ zones }: { zones: Donnees['arrondissements'] }) {
  return (
    <Carte titre="Adhérents par arrondissement">
      <BarresHorizontales lignes={zones.map((z) => ({ nom: z.zone, valeur: z.personnes }))} />
    </Carte>
  );
}

function Anniversaires({ lignes }: { lignes: NonNullable<Donnees['anniversaires']> }) {
  return (
    <Carte titre="Anniversaires à venir">
      {lignes.length === 0 ? (
        <p className="text-sm text-encre-500">Aucun anniversaire sous 15 jours.</p>
      ) : (
        <ul className="grid gap-x-8 gap-y-0.5 text-sm sm:grid-cols-2">
          {lignes.map((ligne, index) => {
            // L'identite se compose AVANT le rendu : un nom absent
            // laissait deux espaces avant le point median.
            const identite = [ligne.prenom, ligne.nom].filter(Boolean).join(' ');
            // L'age et l'activite se lisent du meme gris : ce sont deux
            // precisions sur la personne, pas deux niveaux d'information.
            const precisions = [ligne.age !== null ? `(${ligne.age} ans)` : null, ligne.activites]
              .filter(Boolean)
              .join(' · ');

            return (
              <li key={`${ligne.jour}-${ligne.prenom}-${index}`} className="flex items-baseline gap-2">
                <span className="tabulaire shrink-0 text-encre-500">{ligne.jour}</span>
                {/* L'equipe se distingue par la COULEUR du nom, pas par
                    une pastille : la liste doit tenir dans un coup d'oeil. */}
                <span
                  className={`truncate ${
                    ligne.type === 'equipe' ? 'font-semibold text-blue-700' : ''
                  }`}
                >
                  {identite}
                </span>
                {precisions && (
                  <span className="truncate text-xs text-encre-400">{precisions}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Carte>
  );
}

function Equipe({ equipe }: { equipe: Donnees['equipe'] }) {
  // Seul l'EFFECTIF SOUS CONTRAT est montre : le registre porte 119
  // fiches, dont les intervenants d'hier — la repartition de ceux qui
  // travaillent aujourd'hui est la seule qui dise quelque chose.
  const contrat = equipe.sous_contrat;
  const total = Math.max(1, contrat.femmes + contrat.hommes + contrat.sans_civilite);

  // Le complement plutot qu'un second arrondi : deux arrondis
  // independants affichent « 51 % / 50 % » une fois sur deux.
  const pourcentHommes = Math.round((contrat.hommes / total) * 100);
  const pourcentFemmes =
    contrat.sans_civilite === 0
      ? 100 - pourcentHommes
      : Math.round((contrat.femmes / total) * 100);

  // Hommes a gauche, femmes a droite : la pyramide des ages est lue
  // dans cet ordre, la carte voisine ne peut pas l'inverser.
  return (
    <Carte titre="Parité de l’équipe pédagogique">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="tabulaire text-3xl font-semibold" style={{ color: COULEUR_HOMMES }}>
          {pourcentHommes} %
        </span>
        <span className="tabulaire text-3xl font-semibold" style={{ color: COULEUR_FEMMES }}>
          {pourcentFemmes} %
        </span>
      </div>

      <div className="flex h-4 overflow-hidden rounded bg-encre-50">
        <Infobulle
          texte={`Hommes : ${contrat.hommes}`}
          style={{ width: `${(contrat.hommes / total) * 100}%`, background: COULEUR_HOMMES }}
        />
        <Infobulle
          texte={`Femmes : ${contrat.femmes}`}
          className="border-l-2 border-white"
          style={{ width: `${(contrat.femmes / total) * 100}%`, background: COULEUR_FEMMES }}
        />
        {contrat.sans_civilite > 0 && (
          <Infobulle
            texte={`Sans civilité : ${contrat.sans_civilite}`}
            className="border-l-2 border-white bg-encre-200"
            style={{ width: `${(contrat.sans_civilite / total) * 100}%` }}
          />
        )}
      </div>

      {/* Meme legende que la pyramide des ages, au mot et au style pres. */}
      <div className="mt-auto flex flex-wrap gap-4 pt-3 text-xs text-encre-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm" style={{ background: COULEUR_HOMMES }} /> Hommes
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm" style={{ background: COULEUR_FEMMES }} /> Femmes
        </span>
        <span className="ml-auto">
          {contrat.sans_civilite > 0 && `${contrat.sans_civilite} sans civilité`}
        </span>
      </div>
    </Carte>
  );
}
